import React, { useState } from 'react';
import { X, Smartphone, Search, User, CheckCircle2, QrCode } from 'lucide-react';
import { useCustomers } from '../context/CustomerContext';
import { useAlert } from '../context/AlertContext';
import { supabase } from '../supabase/config';
import QRScanner from './QRScanner';
import { anonymizeName, anonymizePhone } from '../utils/customer';

const LinkCustomerModal = ({ order, onClose, onLinked }) => {
    const { customers } = useCustomers();
    const { showAlert } = useAlert();
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [isScannerOpen, setIsScannerOpen] = useState(false);

    const filteredCustomers = customers.filter(c => {
        const fullName = `${c.firstName || ''} ${c.lastName || ''}`.toLowerCase();
        return fullName.includes(searchTerm.toLowerCase()) || c.phone?.includes(searchTerm);
    }).slice(0, 5);

    const handleLink = async (customer) => {
        setLoading(true);
        try {
            const fullName = `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'Misafir';
            await supabase.from('orders').update({
                customer_id: customer.id,
                customer_name: fullName,
                updated_at: new Date().toISOString()
            }).eq('id', order.id);

            if (onLinked) onLinked(customer);
            setSelectedCustomer(customer);
            setTimeout(onClose, 1500);
        } catch (err) {
            console.error("Link Error:", err);
            showAlert("Müşteri bağlanırken hata oluştu.", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div onClick={onClose} style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.5rem'
        }}>
            <div onClick={(e) => e.stopPropagation()} className="card glass-premium" style={{
                width: '95vw', maxWidth: '450px', maxHeight: '90vh',
                background: 'var(--bg-main)', overflow: 'hidden',
                display: 'flex', flexDirection: 'column'
            }}>
                <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                            <User size={18} />
                        </div>
                        <h3 style={{ fontWeight: 800, fontSize: '1.25rem' }}>Müşteri Bağla</h3>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'rgba(0,0,0,0.05)',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '8px',
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--text-main)',
                            transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'}
                    >
                        <X size={20} strokeWidth={2.5} />
                    </button>
                </div>

                <div style={{ padding: '1.5rem', overflowY: 'auto' }}>
                    {selectedCustomer ? (
                        <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
                            <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#10b981', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', boxShadow: '0 10px 25px rgba(16, 185, 129, 0.3)' }}>
                                <CheckCircle2 size={40} />
                            </div>
                            <h4 style={{ fontSize: '1.5rem', fontWeight: 900, marginBottom: '0.5rem' }}>
                                {`${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim() || 'Misafir'}
                            </h4>
                            <p style={{ color: 'var(--text-muted)', fontSize: '1rem', fontWeight: 600 }}>Başarıyla Bağlandı!</p>
                            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginTop: '1rem' }}>Puanlar ödeme onaylandığında yüklenecektir.</p>
                        </div>
                    ) : (
                        <>
                            <div style={{ position: 'relative', marginBottom: '1.5rem', display: 'flex', gap: '8px' }}>
                                <div style={{ position: 'relative', flex: 1 }}>
                                    <Search style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--primary)', opacity: 0.6 }} size={18} />
                                    <input
                                        type="text"
                                        placeholder="İsim veya telefon ile ara..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{
                                            width: '100%', padding: '14px 14px 14px 44px',
                                            borderRadius: '16px', border: '1px solid var(--border)',
                                            background: 'var(--bg-card)', fontSize: '1rem', fontWeight: 600,
                                            outline: 'none', transition: 'all 0.2s'
                                        }}
                                        className="focus-primary"
                                        autoFocus
                                    />
                                </div>
                                <button
                                    onClick={() => setIsScannerOpen(true)}
                                    style={{
                                        background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px',
                                        padding: '0 16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: 'var(--primary)', cursor: 'pointer', transition: 'all 0.2s'
                                    }}
                                    className="hover-bg"
                                    title="QR Tara"
                                >
                                    <QrCode size={22} />
                                </button>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '350px', overflowY: 'auto' }}>
                                {filteredCustomers.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '2rem 1rem', background: 'var(--bg-card)', borderRadius: '20px', border: '1px dashed var(--border)' }}>
                                        <p style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Müşteri bulunamadı.</p>
                                    </div>
                                ) : (
                                    filteredCustomers.map(c => (
                                        <div
                                            key={c.id}
                                            onClick={() => handleLink(c)}
                                            style={{
                                                padding: '1rem', borderRadius: '18px', border: '1px solid var(--border)',
                                                cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                transition: 'all 0.2s', background: 'var(--bg-card)'
                                            }}
                                            className="hover-scale"
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'rgba(var(--primary-rgb), 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)' }}>
                                                    <User size={20} />
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 800, fontSize: '0.9375rem' }}>
                                                        {anonymizeName(`${c.firstName || ''} ${c.lastName || ''}`) || 'Misafir'}
                                                    </div>
                                                    <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontWeight: 600 }}>{anonymizePhone(c.phone)}</div>
                                                </div>
                                            </div>
                                            <button style={{
                                                background: 'var(--primary)', color: 'white', border: 'none',
                                                padding: '8px 16px', borderRadius: '10px', fontSize: '0.8125rem', fontWeight: 800,
                                                boxShadow: '0 4px 12px rgba(var(--primary-rgb), 0.2)'
                                            }}>Seç</button>
                                        </div>
                                    ))
                                )}
                            </div>

                            <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '2px dashed var(--border)' }}>
                                <button
                                    onClick={() => onLinked && onLinked(null)}
                                    style={{
                                        width: '100%', padding: '1.125rem', borderRadius: '18px',
                                        background: 'var(--primary)', border: 'none',
                                        color: 'white', fontWeight: 900, cursor: 'pointer',
                                        fontSize: '1rem', transition: 'all 0.2s',
                                        boxShadow: '0 8px 25px rgba(var(--primary-rgb), 0.3)',
                                        textTransform: 'uppercase', letterSpacing: '0.5px'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                    onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                >
                                    Müşteri Yok, Anonim Devam Et
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {isScannerOpen && (
                <QRScanner
                    onScan={(id) => {
                        const c = customers.find(x => x.id === id || x.shortId === id);
                        if (c) {
                            handleLink(c);
                        } else {
                            showAlert("Bu karekoda ait müşteri bulunamadı!", "warning");
                        }
                        setIsScannerOpen(false);
                    }}
                    onClose={() => setIsScannerOpen(false)}
                />
            )}
        </div>
    );
};

export default LinkCustomerModal;
